var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * Base for connections to cross window clients
 */
export default class CrossWindowBase {
    constructor(_config) {
        /**
         * Indicates if there is a connection initiated with a cross window client
         */
        this.isConnected = false;
        if (!_config) {
            throw new Error('No configuration provided');
        }
        if (!_config.scope || !_config.window) {
            throw new Error('The provided configuration is incomplete');
        }
        if (_config.window === window) {
            throw new Error('Cannot use the current window as a target');
        }
        // Set the config
        this._config = _config;
        // Prevent Vue from trying to observe a cross-domain address,
        // as this will lead to a security error on modern browsers
        Object.freeze(this._config);
        this.connecting = new Promise((resolve, reject) => {
            this._resolveConnecting = resolve;
            this._rejectConnecting = reject;
        });
        // Start listening to events on its own window
        // (the _config.window is used to post messages to the other peer's listener)
        window.addEventListener('message', (ev) => this._onMessage(ev));
        // Send initial handshake message
        this._sendRawMessage({
            _isOpenticket: true,
            init: 'SYN',
            scope: this._config.scope,
        });
    }
    /**
     * Internal handler for messages to do basic checks and filter spam
     * @param event - The event that is received
     */
    _onMessage(event) {
        return __awaiter(this, void 0, void 0, function* () {
            // Discard other messages
            if (!event.data || !event.data._isOpenticket) {
                return;
            }
            // Clients can only interact with non-clients
            if (event.data.scope === 'client' && this._config.scope === 'client') {
                return;
            }
            // Non-clients can only interact with clients
            if (event.data.scope !== 'client' && this._config.scope !== 'client') {
                return;
            }
            // Discard if the origin of the event is different from the target window
            if (event.source !== this._config.window && process.env.NODE_ENV !== 'test') {
                return;
            }
            // Check if the message is an `init` message
            if (event.data.init) {
                if (event.data.init === 'SYN') {
                    this._sendMessage({ init: 'SYN-ACK', payload: this._initData });
                }
                else if (event.data.init === 'SYN-ACK') {
                    yield this.onConnect(event.data.payload);
                    this._sendMessage({ init: 'ACK', payload: this._initData });
                    this.isConnected = true;
                    this._resolveConnecting();
                }
                else if (event.data.init === 'ACK') {
                    yield this.onConnect(event.data.payload);
                    this.isConnected = true;
                    this._resolveConnecting();
                }
                return;
            }
            // Pass the message to the implemented handler
            yield this.onMessage(event);
        });
    }
    /**
     * This method will be called when the connection is established
     * @param data - The data that is received
     */
    onConnect(data) {
        return __awaiter(this, void 0, void 0, function* () {
            // Implement this method in the super class
        });
    }
    /**
     * This method should be implemented by classes using this one,
     * it will be called when a message arrives
     * @param event - The event that is received
     */
    onMessage(event) {
        return __awaiter(this, void 0, void 0, function* () {
            // Implement this method in the super class
        });
    }
    /**
     * Notify the peer of an error that occurred
     * @param error - A string containing the error desription
     */
    _sendError(error) {
        this._sendMessage({ error });
    }
    /**
     * Internal send method
     * @param data - Payload of the message
     */
    _sendMessage(data) {
        this._sendRawMessage(Object.assign({ _isOpenticket: true, scope: this._config.scope }, data));
    }
    /**
     * Sends a raw message to the specified window
     * @param data - Payload of the message
     */
    _sendRawMessage(data) {
        this._config.window.postMessage(data, '*');
    }
}
//# sourceMappingURL=base.js.map